import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { showAuthAlert } from '../../utils/authAlerts';
import { useNavigate } from 'react-router-dom';
import { reportMovie, reportShow } from '../../services/api';
import { trackInteraction } from '../../utils/hypeRatingClient';

const ReportDialog = ({ open, onClose, item, type }) => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [reporting, setReporting] = useState(false);

  const reportReasons = [
    'Broken streaming link',
    'Incorrect movie/show information',
    'Content not available',
    'Copyright violation',
    'Other technical issue'
  ];

  const handleReport = async () => {
    if (!user) {
      showAuthAlert(addNotification, navigate, 'REPORT');
      onClose();
      return;
    }
    
    if (!reason.trim() || reporting) return;
    
    setReporting(true);
    try {
      const reportData = {
        reason: reason,
        description: description.trim() || null,
        reportedBy: user.id,
      };

      if (type === 'movie') {
        reportData.movieID = item.id;
        await reportMovie(reportData);
        
        // Track HypeRating interaction
        await trackInteraction({
          movieID: item.id,
          contentType: 'MOVIE',
          action: 'report',
          userID: user.id,
          sessionID: sessionStorage.getItem(`movieSession_${item.id}`) || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          deviceType: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop',
          platform: 'web',
          metadata: JSON.stringify({
            source: 'report_dialog',
            reason: reason,
            contentName: item.name,
            timestamp: Date.now()
          }),
          isAnonymous: false
        }, true);
      } else {
        reportData.showID = item.id;
        await reportShow(reportData);
        
        // Track HypeRating interaction
        await trackInteraction({
          showID: item.id,
          contentType: 'SHOW',
          action: 'report',
          userID: user.id,
          sessionID: sessionStorage.getItem(`showSession_${item.id}`) || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          deviceType: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop',
          platform: 'web',
          metadata: JSON.stringify({
            source: 'report_dialog',
            reason: reason,
            contentName: item.name,
            timestamp: Date.now()
          }),
          isAnonymous: false
        }, true);
      }
      
      onClose();
      setReason('');
      setDescription('');
      
      addNotification({
        type: 'success',
        message: `${type === 'movie' ? 'Movie' : 'Show'} reported successfully. Track status in My Reports.`,
      });
    } catch (error) {
      addNotification({
        type: 'error',
        message: `Failed to report ${type}. Please try again.`,
      });
    } finally {
      setReporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Report {type === 'movie' ? 'Movie' : 'Show'}</DialogTitle>
      <DialogContent>
        {!user ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please sign in to report issues with content.
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Help us improve by reporting issues with "{item?.name}"
          </Typography>
        )}
        
        {user && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              What's wrong?
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              {reportReasons.map((reportReason) => (
                <Button
                  key={reportReason}
                  variant={reason === reportReason ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setReason(reportReason)}
                  sx={{ 
                    mr: 1, 
                    mb: 1,
                    textTransform: 'none',
                    fontSize: '0.875rem'
                  }}
                >
                  {reportReason}
                </Button>
              ))}
            </Box>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Additional details (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ mt: 1 }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {user ? (
          <Button 
            onClick={handleReport} 
            variant="contained"
            color="error"
            disabled={!reason.trim() || reporting}
          >
            {reporting ? 'Reporting...' : 'Report Issue'}
          </Button>
        ) : (
          <Button 
            onClick={() => {
              showAuthAlert(addNotification, navigate, 'REPORT');
              onClose();
            }}
            variant="contained"
            color="primary"
          >
            Sign In to Report
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportDialog;